import { ComponentType, HTMLInputTypeAttribute, ReactNode } from 'react';
import { useStore } from '@tanstack/react-form';
import { cn } from '@new/lib';
import { useFieldContext } from './form-context';
import { ErrorMessage } from '@new/common/ui/ErrorMessage/ErrorMessage';

export type LabelPlacement = 'up' | 'right';
export type RenderLabel = ReactNode | ((label: string) => ReactNode);

export interface FormFieldProps {
  label: string;
  labelPlacement?: LabelPlacement;
  renderLabel?: RenderLabel;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const LABEL_PLACEMENT: Record<LabelPlacement, string> = {
  up: 'flex-col items-start gap-3',
  right: 'flex-row-reverse justify-end items-center gap-2',
};

export const createFormField = (
  Component: ComponentType<any>,
  type: HTMLInputTypeAttribute = 'text'
) => {
  const Input = (props: FormFieldProps) => {
    const { label, labelPlacement = 'up', renderLabel, className, ...rest } = props;
    const field = useFieldContext<string>();
    const errors = useStore(field.store, (state) => {
      return state.meta.isTouched ? state.meta.errors : [];
    });
    const Label =
      typeof renderLabel === 'function' ? (
        renderLabel(label)
      ) : (
        <span className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-l4)]! medium text-(--neutral-black-NB900)">
          {label}
        </span>
      );

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <div className={cn('form-field flex', LABEL_PLACEMENT[labelPlacement])}>
          {Label}
          <Component
            value={field.state.value}
            onChange={field.handleChange}
            onBlur={field.handleBlur}
            hasError={errors.length > 0}
            type={type}
            {...rest}
          />
        </div>

        <ErrorMessage errors={errors} />
      </div>
    );
  };

  return Input;
};
